import React, {PropsWithChildren} from "react";
import {Button} from "../../../basic-ui-components/button/base/button.component";
import {TextInputBox} from "../../../basic-ui-components/form/textinput.box";
import { observer } from "mobx-react";
import { observable, runInAction, makeObservable } from "mobx";
import styled from "styled-components"
import { ButtonPreset, ButtonSizeVariant, ButtonIconSize, ButtonStroke, IButtonIconProps, ButtonLabelOverflow } from "../../../basic-ui-components/button/base/button-props.interface";
import { OrbitComponent } from "../../base-components/orbit-component/orbit-component";
import {Label} from "../boxes/label";
import {ShowcaseContentContainerBox} from "../boxes/showcase-content-container/showcase-content-container.box";
import {ShowcaseConfigurationBox} from "../boxes/showcase-configuration/showcase-configuration.box";

const ButtonWrapper = styled.div`
    border: 1px dashed ${({theme}) => theme.colors.text};
    padding: 8px;
    min-width: 400px;
    @media ${({theme}) => theme.mediaQuery.sAndBelow} {
        min-width: 80%;
    }
`;

@observer
export class ButtonsShowcase extends OrbitComponent {
    constructor(props: PropsWithChildren) {
        super(props);

        makeObservable(this);
    }

    @observable.ref
    preset: ButtonPreset = "special";
    @observable.ref
    sizeVariant: ButtonSizeVariant = "m";
    @observable.ref
    withIcon = false;
    @observable.ref
    iconSizeVariant: ButtonIconSize = "large";
    @observable.ref
    stroke: ButtonStroke | undefined = undefined;
    @observable.ref
    _iconComponent: IButtonIconProps["Component"]
    @observable.ref
    iconPlacement: IButtonIconProps["placement"] = "left";
    @observable.ref
    rounded: boolean | undefined = undefined;
    @observable.ref
    elevated: boolean | undefined = undefined;
    @observable.ref
    labelOverflow: ButtonLabelOverflow = "expand";
    @observable.ref
    fullWidth = false;
    @observable.ref
    noRemUnits = false;
    @observable.ref
    disabled = false;
    @observable.ref
    skeleton = false;
    @observable.ref
    buttonLabel = "Button Label";

    get iconComponent(): IButtonIconProps["Component"] {
        if (!this._iconComponent) {
            return this.services.theme.currentTheme.icons.settingsIcon;
        } else {
            return this._iconComponent;
        }

    }

    set iconComponent(value: IButtonIconProps["Component"]) {
        runInAction(() => {
            this._iconComponent = value;
        });

    }

    componentDidMount() {
        super.componentDidMount();
    }


    render() {
        return <ShowcaseContentContainerBox>
            <ShowcaseConfigurationBox>
                <h3>PRESET: </h3>
                <div>
                    {["primary", "secondary", "special", "info", "warning", "flatStroked"].map((color) =>
                        <div key={color}>
                            <input
                                type="radio" name="color" id={"color_" + color} value={color}
                                checked={this.preset === color}
                                onChange={() => {
                                    runInAction(() => {
                                        this.preset = color as ButtonPreset;
                                    })
                                }}
                            />
                            <Label htmlFor={"color_" + color}>{color}</Label>
                        </div>
                    )}
                </div>
            </ShowcaseConfigurationBox>
            <ShowcaseConfigurationBox>
                <h3>Size Variant: </h3>
                <div>
                    {["xl", "l", "m", "s", "xs"].map((sizeVariant) =>
                        <div key={sizeVariant}>
                            <input
                                type="radio" name="sizeVariant" id={"sizeVariant_" + sizeVariant}
                                value={sizeVariant} checked={this.sizeVariant === sizeVariant}
                                onChange={() => {
                                    runInAction(() => {
                                        this.sizeVariant = sizeVariant as ButtonSizeVariant;
                                    })
                                }}
                            />
                            <Label htmlFor={"sizeVariant_" + sizeVariant}>{sizeVariant}</Label>
                        </div>
                    )}
                </div>
                <h3>Stroke: </h3>
                <div>
                    {["heavy", "light", "none", "undefined"].map((stroke) =>
                        <div key={stroke}>
                            <input
                                type="radio" name="stroke" id={"stroke_" + stroke} value={stroke}
                                checked={(stroke === "undefined" && this.stroke === undefined) || this.stroke === stroke}
                                onChange={(ev) => {
                                    runInAction(() => {
                                        this.stroke = ev.target.value !== "undefined" ? ev.target.value as ButtonStroke : undefined;
                                    })
                                }}
                            />
                            <Label htmlFor={"stroke_" + stroke}>{stroke}</Label>
                        </div>
                    )}
                </div>
            </ShowcaseConfigurationBox>
            <ShowcaseConfigurationBox>
                <h3>Icon Component: </h3>
                <div>
                    <input
                        type="checkbox" name="withIcon" id="withIcon" value="withIcon"
                        checked={this.withIcon === true}
                        onChange={() => {
                            runInAction(() => {
                                this.withIcon = !this.withIcon;
                            })
                        }}
                    />
                    <Label htmlFor={"withIcon"}>With Icon</Label>
                </div>
                <div style={{height: "16px"}}></div>
                <div>
                    {[
                        {name: "AddIcon", component: this.services.theme.currentTheme.icons.addIcon},
                        {name: "BackIcon", component: this.services.theme.currentTheme.icons.backIcon},
                        {name: "CloseIcon", component: this.services.theme.currentTheme.icons.closeIcon},
                        {name: "InfoIcon", component: this.services.theme.currentTheme.icons.infoIcon},
                        {name: "SettingsIcon", component: this.services.theme.currentTheme.icons.settingsIcon}
                    ].map((icon) =>
                        <div key={icon.name}>
                            <input
                                type="radio" name="iconComponent" id={"iconComponent_" + icon.name}
                                value={icon.name} checked={this.iconComponent === icon.component}
                                onChange={() => this.iconComponent = icon.component}
                            />
                            <Label htmlFor={"iconComponent_" + icon.name}>{icon.name}</Label>
                        </div>
                    )}
                </div>
            </ShowcaseConfigurationBox>
            <ShowcaseConfigurationBox>
                <h3>Icon Size Variant: </h3>
                <div>
                    {["large", "regular", "small"].map((iconSizeVariant) =>
                        <div key={iconSizeVariant}>
                            <input
                                type="radio" name="iconSizeVariant" id={"iconSizeVariant_" + iconSizeVariant}
                                value={iconSizeVariant} checked={this.iconSizeVariant === iconSizeVariant}
                                onChange={() => {
                                    runInAction(() => {
                                        this.iconSizeVariant = iconSizeVariant as ButtonIconSize;
                                    })
                                }}
                            />
                            <Label htmlFor={"iconSizeVariant_" + iconSizeVariant}>{iconSizeVariant}</Label>
                        </div>
                    )}
                </div>
                <h3>Icon Placement: </h3>
                <div>
                    {["left", "right"].map((iconPlacement) =>
                        <div key={iconPlacement}>
                            <input
                                type="radio" name="iconPlacement" id={"iconPlacement_" + iconPlacement}
                                value={iconPlacement} checked={this.iconPlacement === iconPlacement}
                                onChange={() => {
                                    runInAction(() => {
                                        this.iconPlacement = iconPlacement as IButtonIconProps["placement"];
                                    })
                                }}
                            />
                            <Label htmlFor={"iconPlacement_" + iconPlacement}>{iconPlacement}</Label>
                        </div>
                    )}
                </div>
            </ShowcaseConfigurationBox>
            <ShowcaseConfigurationBox>
                <div>
                    <h3>Rounded</h3>
                    {["true", "false", "undefined"].map((rounded) =>
                        <div key={rounded}>
                            <input
                                type="radio" name="rounded" id={"rounded_" + rounded} value={rounded}
                                checked={(rounded === "undefined" && this.rounded === undefined) || (rounded === "true" && this.rounded === true) || (rounded === "false" && this.rounded === false)}
                                onChange={(ev) => {
                                    runInAction(() => {
                                        this.rounded = ev.target.value === "undefined" ? undefined : (ev.target.value === "true" ? true : false);
                                    })
                                }}
                            />
                            <Label htmlFor={"rounded_" + rounded}>{rounded}</Label>
                        </div>
                    )}
                </div>
                <div style={{height: "16px"}}></div>
                <div>
                    <h3>Elevated</h3>
                    {["true", "false", "undefined"].map((elevated) =>
                        <div key={elevated}>
                            <input
                                type="radio" name="elevated" id={"elevated_" + elevated} value={elevated}
                                checked={(elevated === "undefined" && this.elevated === undefined) || (elevated === "true" && this.elevated === true) || (elevated === "false" && this.elevated === false)}
                                onChange={(ev) => {
                                    runInAction(() => {
                                        this.elevated = ev.target.value === "undefined" ? undefined : (ev.target.value === "true" ? true : false);
                                    })
                                }}
                            />
                            <Label htmlFor={"elevated_" + elevated}>{elevated}</Label>
                        </div>
                    )}
                </div>
            </ShowcaseConfigurationBox>
            <ShowcaseConfigurationBox>
                <div>
                    <h3>Label Overflow</h3>
                    {["expand", "ellipsis", "clip", "wrap"].map((labelOverflow) =>
                        <div key={labelOverflow}>
                            <input
                                type="radio" name="labelOverflow" id={"labelOverflow_" + labelOverflow}
                                value={labelOverflow} checked={this.labelOverflow === labelOverflow}
                                onChange={() => {
                                    runInAction(() => {
                                        this.labelOverflow = labelOverflow as ButtonLabelOverflow;
                                    })
                                }}
                            />
                            <Label htmlFor={"labelOverflow_" + labelOverflow}>{labelOverflow}</Label>
                        </div>
                    )}
                </div>
            </ShowcaseConfigurationBox>
            <ShowcaseConfigurationBox>
                <div>
                    <input
                        type="checkbox" name="noRemUnits" id="noRemUnits" value="noRemUnits"
                        checked={this.noRemUnits === true}
                        onChange={() => {
                            runInAction(() => {
                                this.noRemUnits = !this.noRemUnits;
                            })
                        }}
                    />
                    <Label htmlFor={"noRemUnits"}>No REM units</Label>
                </div>
                <div>
                    <input
                        type="checkbox" name="fullWidth" id="fullWidth" value="fullWidth"
                        checked={this.fullWidth === true}
                        onChange={() => {
                            runInAction(() => {
                                this.fullWidth = !this.fullWidth;
                            })
                        }}
                    />
                    <Label htmlFor={"fullWidth"}>Full Width</Label>
                </div>
                <div>
                    <input
                        type="checkbox" name="disabled" id="disabled" value="disabled"
                        checked={this.disabled === true}
                        onChange={() => {
                            runInAction(() => {
                                this.disabled = !this.disabled;
                            })
                        }}
                    />
                    <Label htmlFor={"disabled"}>Disabled</Label>
                </div>
                <div>
                    <input
                        type="checkbox" name="skeleton" id="skeleton" value="skeleton"
                        checked={this.skeleton === true}
                        onChange={() => {
                            runInAction(() => {
                                this.skeleton = !this.skeleton;
                            })
                        }}
                    />
                    <Label htmlFor={"skeleton"}>Skeleton</Label>
                </div>
                <div>
                    <TextInputBox
                        style={{fontSize: "1.6rem", width: "150px"}}
                        type="text" name="buttonLabel" id="buttonLabel" value={this.buttonLabel}
                        onChange={(ev) => {
                            runInAction(() => {
                                this.buttonLabel = ev.target.value;
                            })
                        }}
                    />
                </div>
            </ShowcaseConfigurationBox>

            <div style={{flexBasis: "100%"}}>
                <ButtonWrapper>
                    <Button
                        preset={this.preset}
                        stroke={this.stroke}
                        icon={this.withIcon ? {
                            Component: this.iconComponent,
                            size: this.iconSizeVariant,
                            placement: this.iconPlacement
                        } : undefined}
                        sizeVariant={this.sizeVariant}
                        rounded={this.rounded}
                        noRemUnits={this.noRemUnits}
                        fullWidth={this.fullWidth}
                        elevated={this.elevated}
                        labelOverflow={this.labelOverflow}
                        disabled={this.disabled}
                        skeleton={this.skeleton}
                    >
                        {this.buttonLabel}
                    </Button>
                </ButtonWrapper>
            </div>
        </ShowcaseContentContainerBox>;
    }
}
